const chapter = {
  id: 'human-health-disease',
  slug: 'human-health-disease',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Human Health and Disease',
        hindi: 'मानव स्वास्थ्य और रोग',
  },
  estReadMinutes: 65,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '4-5 questions in NEET',
        hindi: 'NEET में 4-5 प्रश्न **संगठन का स्तर** — सेलुलर (पोरिफेरा) | कोशिका-ऊतक (सीलेंटरेटा) | ऊतक-अंग (प्लैटिहेल्मिन्थेस) | अंग-प्रणाली (एनेलिडा से आगे)।',
  },
  syllabusTags: ['Biology in Human Welfare', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'health-disease',
      title: {
        english: 'Health & Common Diseases',
        hindi: 'स्वास्थ्य और सामान्य रोग',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'WHO definition of **health** = state of complete physical, mental & social well-being.',
        hindi: 'WHO की **स्वास्थ्य** की परिभाषा = पूर्ण शारीरिक, मानसिक और सामाजिक कल्याण की स्थिति।',
            },
            {
              english: 'Diseases broadly grouped: **Infectious** (transmissible, by pathogens) + **Non-infectious** (cancer, genetic, lifestyle).',
        hindi: 'रोगों को मोटे तौर पर वर्गीकृत किया गया है: **संक्रामक** (संक्रामक, रोगज़नक़ों द्वारा) + **गैर-संक्रामक** (कैंसर, आनुवंशिक, जीवनशैली)।',
            },
            {
              english: 'Pathogens enter via air (cough/sneeze), water, food, vectors, sexual contact.',
        hindi: 'रोगजनक हवा (cough/sneeze), पानी, भोजन, वैक्टर, यौन संपर्क के माध्यम से प्रवेश करते हैं।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Disease', 'Pathogen', 'Type', 'Key feature'],
          rows: [
            [
              'Typhoid',
              'Salmonella typhi',
              'Bacterial',
              'Sustained high fever; **Widal test** for diagnosis; **Typhoid Mary** (asymptomatic carrier)',
            ],
            ['Pneumonia', 'Streptococcus pneumoniae / Haemophilus influenzae', 'Bacterial', 'Alveoli filled with fluid; lips/nails grey-blue'],
            ['Common cold', 'Rhino viruses', 'Viral', 'Nose/respiratory passage (not lungs)'],
            [
              'Malaria',
              'Plasmodium (vivax/malariae/falciparum)',
              'Protozoan',
              'Female *Anopheles* vector; haemozoin → chills + fever every 3-4 days; falciparum = malignant',
            ],
            ['Amoebiasis', 'Entamoeba histolytica', 'Protozoan', 'Constipation, abdominal pain, bloody stools'],
            ['Ascariasis', 'Ascaris lumbricoides', 'Helminth', 'Internal bleeding, muscular pain'],
            ['Elephantiasis (Filariasis)', 'Wuchereria bancrofti / W. malayi', 'Helminth', 'Vector = female *Culex*; chronic lymphatic vessel inflammation'],
            ['Ringworm', 'Microsporum / Trichophyton / Epidermophyton', 'Fungal', 'Dry scaly lesions; skin/nail/scalp'],
          ],
        },
      ],
    },
    {
      id: 'malaria-cycle',
      title: {
        english: 'Malaria Life Cycle',
        hindi: 'मलेरिया जीवन चक्र',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Vector — **female *Anopheles*** mosquito.',
        hindi: 'सदिश - **मादा *एनोफिलिस*** मच्छर।',
            },
            {
              english: 'Sporozoites injected with saliva → human liver → multiply → release **merozoites** → enter RBCs → multiply → rupture RBC → release **haemozoin** (toxin) → recurring chills + high fever every 3-4 days.',
        hindi: 'स्पोरोज़ोइट्स को लार के साथ इंजेक्ट किया जाता है → मानव यकृत → गुणा → रिलीज **मेरोज़ोइट्स** → आरबीसी में प्रवेश → गुणा → आरबीसी टूटना → रिलीज **हेमोज़ोइन ** (विष) → बार-बार ठंड लगना + हर 3-4 दिनों में तेज बुखार।',
            },
            {
              english: 'Mosquito bites infected person → gametocytes → fertilisation + development in mosquito gut → **sporozoites** in salivary glands.',
        hindi: 'मच्छर के काटने से संक्रमित व्यक्ति → गैमेटोसाइट्स → निषेचन + मच्छर आंत में विकास → **स्पोरोज़ोइट्स** लार ग्रंथियों में।',
            },
            {
              english: 'Two hosts: **human (intermediate, asexual)** + **mosquito (definitive, sexual)** → digenetic life cycle.',
        hindi: 'दो मेजबान: **मानव (मध्यवर्ती, अलैंगिक)** + **मच्छर (निश्चित, यौन)** → पाचन जीवन चक्र। **जन्मजात (गैर-विशिष्ट)** - जन्म के समय मौजूद। 4 प्रकार की बाधाएँ:',
            },
          ],
        },
      ],
    },
    {
      id: 'immunity',
      title: {
        english: 'Immunity',
        hindi: 'प्रतिरक्षा',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Innate (non-specific)** — present at birth. 4 types of barriers:',
        hindi: '**जन्मजात (गैर-विशिष्ट)** - जन्म के समय मौजूद। 4 प्रकार की बाधाएँ:',
            },
            {
              english: '• **Physical** — skin, mucous lining of respiratory/GI/uro-genital tract.',
        hindi: '• **शारीरिक** - त्वचा, respiratory/GI/यूरो-जननांग पथ की श्लेष्मा परत।',
            },
            {
              english: '• **Physiological** — acid in stomach, saliva in mouth, tears (lysozyme).',
        hindi: '• **शारीरिक** - पेट में एसिड, मुंह में लार, आंसू (लाइसोजाइम)।',
            },
            {
              english: '• **Cellular** — neutrophils, monocytes, NK cells, macrophages → **phagocytosis**.',
        hindi: '• **सेलुलर** - न्यूट्रोफिल, मोनोसाइट्स, एनके कोशिकाएं, मैक्रोफेज → **फैगोसाइटोसिस**।',
            },
            {
              english: '• **Cytokine** — virus-infected cells secrete **interferons** → protect non-infected cells.',
        hindi: '• **साइटोकिन** - वायरस से संक्रमित कोशिकाएं **इंटरफेरॉन** का स्राव करती हैं → गैर-संक्रमित कोशिकाओं की रक्षा करती हैं। **अधिग्रहित (विशिष्ट)** — रोगज़नक़-विशिष्ट; प्राथमिक प्रतिक्रिया कम है; ** प्रतिक्रिया** तेज़ और उच्च (मेमोरी सेल) है। • **ह्यूमोरल इम्युनिटी** - **बी-लिम्फोसाइट्स** **एंटीबॉडी** ​​का उत्पादन करते हैं (Y-आकार, **2 H + 2 L चेन** = H₂L₂); 5 वर्ग: **आईजीए, आईजीडी, आईजीई, आईजीजी, आईजीएम**।',
            },
            {
              english: '**Acquired (specific)** — pathogen-specific; primary response is low; **secondary/anamnestic response** is fast & high (memory cells).',
        hindi: '**अधिग्रहित (विशिष्ट)** - रोगज़नक़-विशिष्ट; प्राथमिक प्रतिक्रिया कम है; **secondary/anamnestic प्रतिक्रिया** तेज़ और उच्च (मेमोरी सेल) है।',
            },
            {
              english: '• **Humoral immunity** — **B-lymphocytes** produce **antibodies** (Y-shaped, **2 H + 2 L chains** = H₂L₂); 5 classes: **IgA, IgD, IgE, IgG, IgM**.',
        hindi: '• **हास्य प्रतिरक्षा** - **बी-लिम्फोसाइट्स** **एंटीबॉडी** ​​का उत्पादन करते हैं (Y-आकार, **2 H + 2 L चेन** = H₂L₂); 5 वर्ग: **आईजीए, आईजीडी, आईजीई, आईजीजी, आईजीएम**।',
            },
            {
              english: '• **Cell-mediated immunity (CMI)** — **T-lymphocytes** directly attack pathogens; responsible for **graft rejection** → tissue matching + blood-group matching essential before transplant.',
        hindi: '• **सेल-मध्यस्थ प्रतिरक्षा (सीएमआई)** - **टी-लिम्फोसाइट्स** सीधे रोगजनकों पर हमला करते हैं; **ग्राफ्ट अस्वीकृति** के लिए जिम्मेदार → प्रत्यारोपण से पहले ऊतक मिलान + रक्त-समूह मिलान आवश्यक है।',
            },
            {
              english: 'Two more dichotomies:',
        hindi: 'दो और द्वंद्व:',
            },
            {
              english: '• **Active (slow but lasting; injection of live/killed/antigens)** vs **Passive (ready-made antibodies; foetus via placenta IgG / colostrum IgA / antivenom)**.',
        hindi: '• **सक्रिय (धीमा लेकिन स्थायी; live/killed/एंटीजन का इंजेक्शन)** बनाम **निष्क्रिय (तैयार एंटीबॉडी; प्लेसेंटा आईजीजी / कोलोस्ट्रम आईजीए / एंटीवेनम के माध्यम से भ्रूण)**।',
            },
            {
              english: '• **Vaccination & immunisation** — based on memory of immune system; first principle by **Edward Jenner (1798) — smallpox**.',
        hindi: '• **टीकाकरण और प्रतिरक्षण** - प्रतिरक्षा प्रणाली की स्मृति पर आधारित; **एडवर्ड जेनर (1798) द्वारा पहला सिद्धांत - चेचक**।',
            },
          ],
        },
      ],
    },
    {
      id: 'allergy-autoimmunity',
      title: {
        english: 'Allergy, Autoimmunity & Lymphoid Organs',
        hindi: 'एलर्जी, स्वप्रतिरक्षा और लसीकाभ अंग',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Allergy** — exaggerated response to certain antigens (allergens) in environment (mites, pollen, dander, drugs).',
        hindi: '**एलर्जी** - पर्यावरण में कुछ एंटीजन (एलर्जी) के प्रति अतिरंजित प्रतिक्रिया (घुन, पराग, रूसी, दवाएं)।',
            },
            {
              english: '• Antibody = **IgE**; chemicals released = **histamine + serotonin** by mast cells → sneezing, watery eyes, running nose, wheezing.',
        hindi: '• एंटीबॉडी = **आईजीई**; मस्तूल कोशिकाओं द्वारा उत्सर्जित रसायन = **हिस्टामाइन + सेरोटोनिन** → छींक आना, आँखों से पानी आना, नाक बहना, घरघराहट।',
            },
            {
              english: '• Drugs: **anti-histamine, adrenalin, steroids**.',
        hindi: '• दवाएं: **एंटी-हिस्टामाइन, एड्रेनालाईन, स्टेरॉयड**।',
            },
            {
              english: '**Autoimmunity** — immune system attacks self → **rheumatoid arthritis**, lupus.',
        hindi: '**ऑटोइम्यूनिटी** - प्रतिरक्षा प्रणाली स्वयं पर हमला करती है → **संधिशोथ**, ल्यूपस।',
            },
            {
              english: '**Lymphoid organs** — primary: **bone marrow + thymus** (T-cell maturation; large at birth, shrinks with age). Secondary: **spleen, lymph nodes, tonsils, Peyer\'s patches (small intestine), appendix, MALT** (50 % of lymphoid tissue).',
        hindi: '**लिम्फोइड अंग** - प्राथमिक: **अस्थि मज्जा + थाइमस** (टी-कोशिका परिपक्वता; जन्म के समय बड़ा, उम्र के साथ सिकुड़ता है)। माध्यमिक: **प्लीहा, लिम्फ नोड्स, टॉन्सिल, पियर्स पैच (छोटी आंत), अपेंडिक्स, MALT** (लिम्फोइड ऊतक का 50%)।',
            },
          ],
        },
      ],
    },
    {
      id: 'aids',
      title: {
        english: 'AIDS — HIV',
        hindi: 'एड्स - एचआईवी',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**AIDS** = Acquired Immuno Deficiency Syndrome; first reported **1981 (USA)**; now > 25 million deaths globally.',
        hindi: '**एड्स** = एक्वायर्ड इम्यूनो डेफिशिएंसी सिंड्रोम; पहली रिपोर्ट **1981 (यूएसए)**; अब वैश्विक स्तर पर 25 मिलियन से अधिक मौतें।',
            },
            {
              english: '**HIV (Human Immunodeficiency Virus)** = retrovirus with two ssRNA molecules + reverse transcriptase + envelope.',
        hindi: '**एचआईवी (ह्यूमन इम्युनोडेफिशिएंसी वायरस)** = दो एसएसआरएनए अणुओं वाला रेट्रोवायरस + रिवर्स ट्रांसक्रिपटेस + लिफाफा।',
            },
            {
              english: 'Transmission: **sexual contact, contaminated blood / needles, infected mother → child (placenta)**. NOT by handshake/hugging/sharing food.',
        hindi: 'संचरण: **यौन संपर्क, दूषित रक्त/सुइयां, संक्रमित मां → बच्चा (प्लेसेंटा)**। handshake/hugging/भोजन साझा करने से नहीं।',
            },
            {
              english: 'Mechanism: enters macrophage → reverse transcription → DNA integrated into host chromosome (provirus) → produces virus particles → also enters **helper T-lymphocytes (CD4 cells)** → progressive depletion → immune system collapses → opportunistic infections (TB, Toxoplasma, fungal, viral).',
        hindi: 'तंत्र: मैक्रोफेज में प्रवेश करता है → रिवर्स ट्रांसक्रिप्शन → डीएनए होस्ट क्रोमोसोम (प्रोवायरस) में एकीकृत होता है → वायरस कण पैदा करता है → सहायक टी-लिम्फोसाइट्स (सीडी 4 कोशिकाओं) में भी प्रवेश करता है ** → प्रगतिशील कमी → प्रतिरक्षा प्रणाली ढह जाती है → अवसरवादी संक्रमण (टीबी, टोक्सोप्लाज्मा, फंगल, वायरल)।',
            },
            {
              english: 'Diagnosis: **ELISA**; confirmed by **Western blot** or PCR for early detection.',
        hindi: 'निदान: **एलिसा**; शीघ्र पता लगाने के लिए **वेस्टर्न ब्लॉट** या पीसीआर द्वारा पुष्टि की गई।',
            },
            {
              english: 'Treatment: **anti-retroviral therapy (ART)** — only prolongs life; no cure.',
        hindi: 'उपचार: **एंटी-रेट्रोवायरल थेरेपी (ART)** - केवल जीवन को लम्बा खींचता है; कोई इलाज नहीं. NACO (राष्ट्रीय एड्स नियंत्रण संगठन) + WHO निवारक उपाय (कंडोम, जांच किया गया रक्त, जागरूकता) करता है।',
            },
            {
              english: 'NACO (National AIDS Control Organisation) + WHO take preventive measures (condoms, screened blood, awareness).',
        hindi: 'NACO (राष्ट्रीय एड्स नियंत्रण संगठन) + WHO निवारक उपाय (कंडोम, जांच किया गया रक्त, जागरूकता) करता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'cancer',
      title: {
        english: 'Cancer',
        hindi: 'कैंसर',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Normal cells: **contact inhibition** stops growth on touch with neighbours; cancer cells lose this property → **uncontrolled, invasive growth**.',
        hindi: 'सामान्य कोशिकाएं: **संपर्क अवरोध** पड़ोसियों के संपर्क में आने से विकास रुक जाता है; कैंसर कोशिकाएं इस गुण को खो देती हैं → **अनियंत्रित, आक्रामक वृद्धि**। **सौम्य ट्यूमर** — सीमित; थोड़ा नुकसान. **घातक**-प्रजनन करने वाला, आक्रमण करने वाला और ऊतकों को नुकसान पहुंचाने वाला; **मेटास्टैसिस** (कोशिकाएं रक्त के माध्यम से दूर के स्थानों तक पहुंचती हैं) → सबसे खतरनाक संपत्ति।',
            },
            {
              english: '**Benign tumour** — confined; little damage. **Malignant** — proliferative, invade & damage tissues; **metastasis** (cells reach distant sites via blood) → most feared property.',
        hindi: '**सौम्य ट्यूमर** — सीमित; थोड़ा नुकसान. **घातक**-प्रजनन करने वाला, आक्रमण करने वाला और ऊतकों को नुकसान पहुंचाने वाला; **मेटास्टैसिस** (कोशिकाएं रक्त के माध्यम से दूर के स्थानों तक पहुंचती हैं) → सबसे खतरनाक संपत्ति।',
            },
            {
              english: 'Causes (**carcinogens**) — physical (X-rays, γ-rays, UV), chemical (tobacco — major cause of lung cancer), biological (oncogenic viruses with **viral oncogenes**); cellular **proto-oncogenes / c-onc** under certain conditions become active oncogenes.',
        hindi: 'कारण (**कार्सिनोजेन्स**) - भौतिक (एक्स-रे, γ-किरणें, यूवी), रासायनिक (तंबाकू - फेफड़ों के कैंसर का प्रमुख कारण), जैविक (**वायरल ऑन्कोजीन** के साथ ऑन्कोजेनिक वायरस); सेलुलर **प्रोटो-ओन्कोजीन / सी-ऑनसी** कुछ शर्तों के तहत सक्रिय ऑन्कोजीन बन जाते हैं।',
            },
            {
              english: 'Types: carcinoma (epithelial), sarcoma (connective tissue), leukaemia (blood), lymphoma (lymph), myeloma (bone marrow).',
        hindi: 'प्रकार: कार्सिनोमा (उपकला), सार्कोमा (संयोजी ऊतक), ल्यूकेमिया (रक्त), लिम्फोमा (लिम्फ), मायलोमा (अस्थि मज्जा)।',
            },
            {
              english: 'Detection: **biopsy** (histopathology), **MRI, CT, X-ray, radiography**, **monoclonal antibodies against cancer-specific antigens**, blood/bone-marrow tests for leukaemia.',
        hindi: 'जांच: **बायोप्सी** (हिस्टोपैथोलॉजी), **एमआरआई, सीटी, एक्स-रे, रेडियोग्राफी**, **कैंसर-विशिष्ट एंटीजन के खिलाफ मोनोक्लोनल एंटीबॉडी**, ल्यूकेमिया के लिए blood/bone-मज्जा परीक्षण।',
            },
            {
              english: 'Treatment: **surgery, radiation therapy, chemotherapy, immunotherapy** (α-interferon — activates immune system).',
        hindi: 'उपचार: **सर्जरी, विकिरण चिकित्सा, कीमोथेरेपी, इम्यूनोथेरेपी** (α-इंटरफेरॉन - प्रतिरक्षा प्रणाली को सक्रिय करता है)।',
            },
          ],
        },
      ],
    },
    {
      id: 'drugs-alcohol',
      title: {
        english: 'Drugs & Alcohol Abuse',
        hindi: 'नशीले पदार्थ और मद्यपान का दुरुपयोग',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Common abused drugs: **opioids, cannabinoids, coca alkaloids**.',
        hindi: 'आम दुरुपयोग वाली दवाएं: **ओपियोइड, कैनाबिनोइड, कोका एल्कलॉइड**।',
            },
            {
              english: '**Opioids** — **heroin (smack)** = diacetylmorphine → from latex of *Papaver somniferum* (poppy plant). Binds opioid receptors of CNS & GI tract; depressant; slows body functions.',
        hindi: '**ओपियोइड्स** - **हेरोइन (स्मैक)** = डायएसिटाइलमॉर्फिन → *पापावर सोम्निफेरम* (पोस्ता का पौधा) के लेटेक्स से। सीएनएस और जीआई पथ के ओपिओइड रिसेप्टर्स को बांधता है; अवसादजनक; शरीर के कार्यों को धीमा कर देता है।',
            },
            {
              english: '**Cannabinoids** — *Cannabis sativa* (inflorescence). Marijuana, hashish, charas, ganja → affect cardiovascular system.',
        hindi: '**कैनाबिनोइड्स** - *कैनाबिस सैटिवा* (पुष्पक्रम)। मारिजुआना, हशीश, चरस, गांजा → हृदय प्रणाली को प्रभावित करते हैं।',
            },
            {
              english: '**Cocaine (coke/crack)** — *Erythroxylum coca* (S. America). Interferes with **dopamine** transport → euphoria + ↑ energy + hallucinations.',
        hindi: '**कोकीन (coke/crack)** — *एरिथ्रोक्सिलम कोका* (दक्षिण अमेरिका)। **डोपामाइन** परिवहन → उत्साह + ↑ ऊर्जा + मतिभ्रम में हस्तक्षेप करता है।',
            },
            {
              english: 'Other hallucinogens: **LSD (Lysergic Acid Diethylamide)**, datura.',
        hindi: 'अन्य मतिभ्रम: **एलएसडी (लिसेर्जिक एसिड डायथाइलैमाइड)**, धतूरा।',
            },
            {
              english: 'Tobacco — contains **nicotine** → stimulates adrenal → ↑ BP + ↑ heart rate; smoking causes lung cancer, COPD, oral cancer; ↑ CO + ↓ O₂ in blood.',
        hindi: 'तंबाकू - इसमें **निकोटीन** होता है → अधिवृक्क ग्रंथि को उत्तेजित करता है → ↑ बीपी + ↑ हृदय गति; धूम्रपान से फेफड़ों का कैंसर, सीओपीडी, मुंह का कैंसर होता है; ↑ CO + ↓ O₂ रक्त में।',
            },
            {
              english: 'Adolescents are most vulnerable. Warning signs: drop in academic performance, loss of interest in hobbies, withdrawal, isolation, depression.',
        hindi: 'किशोर सबसे अधिक असुरक्षित हैं। चेतावनी के संकेत: शैक्षणिक प्रदर्शन में गिरावट, शौक में रुचि की कमी, वापसी, अलगाव, अवसाद।',
            },
            {
              english: 'Prevention & control: avoid undue peer pressure, education & counselling, seek help from parents & professionals, look for danger signs in friends.',
        hindi: 'रोकथाम और नियंत्रण: साथियों के अनुचित दबाव, शिक्षा और परामर्श से बचें, माता-पिता और पेशेवरों से मदद लें, दोस्तों में खतरे के संकेत देखें।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Antibody = H}_2\\text{L}_2 \\text{ (Y-shape); IgA, IgD, IgE, IgG, IgM}',
      desc: {
        english: '',
        hindi: 'एंटीबॉडी = एच 2 एल 2 (वाई-आकार); आईजीए, आईजीडी, आईजीई, आईजीजी, आईजीएम',
      },
    },
    {
      latex: '\\text{B-cells} \\rightarrow \\text{humoral; T-cells} \\rightarrow \\text{CMI (graft rejection)}',
      desc: {
        english: '',
        hindi: 'बी-कोशिकाएं विनोदी; टी-सेल सीएमआई (ग्राफ्ट अस्वीकृति)',
      },
    },
    {
      latex: '\\text{HIV: retrovirus, 2 ssRNA + RT; targets CD4}^+ \\text{ T-cells}',
      desc: {
        english: '',
        hindi: 'एचआईवी: रेट्रोवायरस, 2 एसएसआरएनए + आरटी; CD4 + T-कोशिकाओं को लक्ष्य करता है',
      },
    },
    {
      latex: '\\text{Diagnosis HIV: ELISA} \\rightarrow \\text{Western blot}',
      desc: {
        english: '',
        hindi: 'एचआईवी का निदान: एलिसा वेस्टर्न ब्लॉट',
      },
    },
    {
      latex: '\\text{Allergy: IgE + mast cells} \\rightarrow \\text{histamine, serotonin}',
      desc: {
        english: '',
        hindi: 'एलर्जी: आईजीई + मस्तूल कोशिकाएं हिस्टामाइन, सेरोटोनिन',
      },
    },
    {
      latex: '\\text{Cancer: loss of contact inhibition + metastasis (most dangerous)}',
      desc: {
        english: '',
        hindi: 'कैंसर: संपर्क अवरोध का नुकसान + मेटास्टेसिस (सबसे खतरनाक)',
      },
    },
    {
      latex: '\\text{Heroin = diacetylmorphine (Papaver somniferum)}',
      desc: {
        english: '',
        hindi: 'हेरोइन = डायएसिटाइलमॉर्फिन (पापावर सोम्निफेरम)',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Vector',
      problem: {
        english: 'Vector for malaria is:',
        hindi: 'मलेरिया का सदिश है:',
      },
      answer: {
        english: 'Female Anopheles mosquito',
        hindi: 'मादा एनोफिलीज मच्छर',
      },
      solution: {
        english: 'Definitive host (sexual phase in mosquito gut).',
        hindi: 'निश्चित मेजबान (मच्छर आंत में यौन चरण)।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Antibody',
      problem: {
        english: 'Antibody involved in allergic reactions is:',
        hindi: 'एलर्जी प्रतिक्रियाओं में शामिल एंटीबॉडी है:',
      },
      answer: {
        english: 'IgE',
        hindi: 'आईजीई',
      },
      solution: {
        english: 'Triggers release of histamine + serotonin from mast cells.',
        hindi: 'यह मास्ट कोशिकाओं से हिस्टामीन और सेरोटोनिन का स्राव करवाता है।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'AIDS',
      problem: {
        english: 'HIV primarily targets:',
        hindi: 'एचआईवी मुख्य रूप से निम्न को लक्षित करता है:',
      },
      answer: {
        english: 'Helper T-lymphocytes (CD4)',
        hindi: 'सहायक टी-लिम्फोसाइट्स (सीडी4)',
      },
      solution: {
        english: 'Progressive depletion → immunodeficiency.',
        hindi: 'प्रगतिशील कमी → इम्युनोडेफिशिएंसी।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Cancer',
      problem: {
        english: 'Spread of cancer cells to distant sites is called:',
        hindi: 'कैंसर कोशिकाओं का दूर-दूर तक फैलना कहलाता है:',
      },
      answer: {
        english: 'Metastasis',
        hindi: 'मेटास्टेसिस',
      },
      solution: {
        english: 'Most dangerous property of malignant tumours.',
        hindi: 'घातक ट्यूमर की सबसे खतरनाक संपत्ति।',
      },
    },
    {
      year: 2019,
      exam: 'NEET',
      tag: 'Diagnosis',
      problem: {
        english: 'Widal test is used to diagnose:',
        hindi: 'विडाल परीक्षण का उपयोग निदान के लिए किया जाता है:',
      },
      answer: {
        english: 'Typhoid',
        hindi: 'टाइफाइड',
      },
      solution: {
        english: 'Detects antibodies against Salmonella typhi.',
        hindi: 'साल्मोनेला टाइफी के खिलाफ एंटीबॉडी का पता लगाता है।',
      },
    },
  ],
  next: {
    slug: 'strategies-food-production',
    title: 'Strategies for Enhancement in Food Production',
  },
};

export default chapter;
